import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { ApplicationState } from 'store';
import { AiManagementState } from 'store/modules/ai_management_store/types';
import { getColumns, getCards, getTeam } from 'store/modules/ai_management_store/actions';
import { taskColor } from 'services/task_appearance';
import Calendar from 'components/molecules/calendar';
import AiManagementCardModal from 'components/organisms/ai_management_card_modal';

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  className: string;
  description: string;
  type: string;
  bidding_id?: number;
}

const DATE_FIELDS: { key: 'deadline_at' | 'opening_at' | 'document_at'; label: string }[] = [
  { key: 'deadline_at', label: 'Prazo' },
  { key: 'opening_at', label: 'Abertura' },
  { key: 'document_at', label: 'Documento' },
];

const AiManagementCalendar: React.FC = () => {
  const dispatch = useDispatch();
  const { cards, filters, request } = useSelector<ApplicationState, AiManagementState>(
    (state) => state.session.ai_management,
  );
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(getTeam());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getColumns(filters));
    dispatch(getCards(filters));
  }, [dispatch, filters]);

  const allCards = useMemo(() => Object.values(cards).flat(), [cards]);

  const events = useMemo<CalendarEvent[]>(
    () =>
      allCards.flatMap((card) =>
        DATE_FIELDS.flatMap((field): CalendarEvent[] => {
          const iso = card.bidding[field.key];
          if (!iso) return [];
          return [
            {
              id: `${card.id}-${field.key}`,
              title: `${field.label} · ${card.bidding.notice_number}`,
              start: moment(moment.parseZone(iso).format('YYYY-MM-DDTHH:mm:ss')).toDate(),
              className: taskColor(iso, false),
              description: `${field.label} · ${card.bidding.notice_number} - ${card.bidding.public_body}`,
              type: 'task',
              bidding_id: card.bidding_id,
            },
          ];
        }),
      ),
    [allCards],
  );

  const selectedCard =
    selectedCardId != null ? allCards.find((card) => card.id === selectedCardId) ?? null : null;

  const handleEventClick = (eventId: string) => setSelectedCardId(Number(eventId.split('-')[0]));

  if (!request.cards.loaded && !request.cards.error) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  if (request.cards.error) {
    return (
      <div className="text-center py-5">
        <p className="text-muted mb-2">Não foi possível carregar as licitações.</p>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => dispatch(getCards(filters))}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="row">
      <Calendar eventsList={events} onTaskClick={handleEventClick} />
      {selectedCard && (
        <AiManagementCardModal card={selectedCard} onClose={() => setSelectedCardId(null)} />
      )}
    </div>
  );
};

export default AiManagementCalendar;
